import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import all components
import MetricsCard from './components/MetricsCard';
import AssetDistributionChart from './components/AssetDistributionChart';
import WarrantyTimelineChart from './components/WarrantyTimelineChart';
import AssetAgeHistogram from './components/AssetAgeHistogram';
import StatusSummaryCards from './components/StatusSummaryCards';
import FilterPanel from './components/FilterPanel';
import ExportPanel from './components/ExportPanel';

const AssetAnalyticsDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [filters, setFilters] = useState({});

  // Key metrics data
  const formatIndianCurrency = (value) => {
    if (value >= 10000000) {
      const crore = value / 10000000;
      return `₹${crore % 1 === 0 ? crore : crore?.toFixed(2)} Cr`;
    } else if (value >= 100000) {
      const lakh = value / 100000;
      return `₹${lakh % 1 === 0 ? lakh : lakh?.toFixed(2)} L`;
    } else if (value >= 1000) {
      const thousand = value / 1000;
      return `₹${thousand % 1 === 0 ? thousand : thousand?.toFixed(2)} K`;
    }
    return `₹${value}`;
  };

  const keyMetrics = [
    {
      title: 'Total Asset Value',
      value: '₹1.68 Cr',
      change: '+3.2%',
      changeType: 'increase',
      icon: 'IndianRupee',
      color: 'primary'
    },
    {
      title: 'Asset Utilization',
      value: '89.3%',
      change: '+1.8%',
      changeType: 'increase',
      icon: 'Activity',
      color: 'success'
    },
    {
      title: 'Warranty Expiring',
      value: '72',
      change: '+12%',
      changeType: 'increase',
      icon: 'AlertTriangle',
      color: 'warning'
    },
    {
      title: 'Replacement Due',
      value: '34',
      change: '-5%',
      changeType: 'decrease',
      icon: 'RefreshCw',
      color: 'accent'
    }
  ];

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate data refresh
    setTimeout(() => {
      setRefreshing(false);
      setLastUpdated(new Date());
    }, 2000);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters updated:', newFilters);
  };

  const formatLastUpdated = (date) => {
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Asset Analytics Dashboard - AssetTracker Pro</title>
        <meta name="description" content="Executive-level asset intelligence interface providing strategic insights and operational metrics for data-driven asset management decisions." />
      </Helmet>
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-layout ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      } mt-16 p-6`}>
        {/* Page Header */}
        <div className="mb-6">
          <Breadcrumb />
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Asset Analytics Dashboard
              </h1>
              <p className="text-muted-foreground">
                Strategic insights and operational metrics for data-driven asset management
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-sm text-muted-foreground">
                Last updated: {formatLastUpdated(lastUpdated)}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                loading={refreshing}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowExportPanel(true)}
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="mb-6">
          <FilterPanel
            onFiltersChange={handleFiltersChange}
            isCollapsed={filtersCollapsed}
            onToggleCollapse={() => setFiltersCollapsed(!filtersCollapsed)}
          />
        </div>

        {/* Key Metrics */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Key Performance Indicators</h2>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="TrendingUp" size={16} />
              <span>Real-time metrics</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {keyMetrics?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                color={metric?.color}
              />
            ))}
          </div>
        </div>

        {/* Status Summary Cards */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Asset Status Overview</h2>
            <Button variant="ghost" size="sm" iconName="ExternalLink">
              View Details
            </Button>
          </div>
          <StatusSummaryCards />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
          {/* Asset Distribution */}
          <AssetDistributionChart />
          
          {/* Warranty Timeline */}
          <WarrantyTimelineChart />
        </div>

        {/* Asset Age Analysis */}
        <div className="mb-8">
          <AssetAgeHistogram />
        </div>

        {/* Data Integration Status */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-card-foreground">Data Integration Status</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span className="text-sm text-success font-medium">Live</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3 p-3 bg-success/5 rounded-lg border border-success/20">
              <Icon name="Database" size={20} className="text-success" />
              <div>
                <div className="font-medium text-card-foreground">ERP System</div>
                <div className="text-sm text-success">Connected • Last sync: 2 min ago</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-success/5 rounded-lg border border-success/20">
              <Icon name="CreditCard" size={20} className="text-success" />
              <div>
                <div className="font-medium text-card-foreground">Financial System</div>
                <div className="text-sm text-success">Connected • Last sync: 5 min ago</div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-warning/5 rounded-lg border border-warning/20">
              <Icon name="Scan" size={20} className="text-warning" />
              <div>
                <div className="font-medium text-card-foreground">Barcode Scanner</div>
                <div className="text-sm text-warning">Partial • Last sync: 1 hour ago</div>
              </div>
            </div>
          </div>
        </div>

        {/* Export Panel */}
        <ExportPanel
          isOpen={showExportPanel}
          onClose={() => setShowExportPanel(false)}
        />
      </main>
    </div>
  );
};

export default AssetAnalyticsDashboard;